import React, { Suspense, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import Text from 'shared/components/Text/Text';
import { useBreakpoints } from 'hooks/useWindowSize';
import { useTheme } from 'styled-components';
import { resolvePath, useNavigate } from 'react-router';
import pages from 'pages';
import { FlagKey } from 'api/types';
import { useFeatureFlags } from 'hooks/useFeatureFlags';
import { FAQPageWrapper, FAQTile, FAQTileContent, FAQTilesWrapper } from './FAQTiles.styles';
import { FAQSearchBar } from '../FAQSearchBar/FAQSearchBar';
import { FAQSearchResults } from '../FAQSearchResults/FAQSearchResults';

const FAQTileGraphic = React.lazy(
  () => import('modules/support/components/FAQTileGraphic/FAQTileGraphic'),
);

export enum FAQCategory {
  PAYING_BILL = 'category:paying_bill',
  PAYMENT_CALCULATIONS = 'category:payment_calculations',
  SYSTEM_PERFORMANCE = 'category:system_performance',
  SYSTEM_CHANGES = 'category:system_changes',
  ACCOUNT = 'category:account',
  REAMORTIZATION = 'category:reamortization',
  VIDEO_RESOURCES = 'videoResources',
  AGREEMENT_TRANSFER = 'category:agreement_transfer',
}

const PAGES_BY_FAQ_CATEGORY = {
  [FAQCategory.ACCOUNT]: pages.ACCOUNT_SUPPORT,
  [FAQCategory.PAYING_BILL]: pages.BILL_PAYMENTS,
  [FAQCategory.PAYMENT_CALCULATIONS]: pages.PAYMENT_CALCULATIONS,
  [FAQCategory.REAMORTIZATION]: pages.REAMORTIZATION,
  [FAQCategory.SYSTEM_CHANGES]: pages.SYSTEM_CHANGES,
  [FAQCategory.SYSTEM_PERFORMANCE]: pages.SYSTEM_PERFORMANCE,
  [FAQCategory.VIDEO_RESOURCES]: pages.VIDEO_RESOURCES,
  [FAQCategory.AGREEMENT_TRANSFER]: pages.AGREEMENT_TRANSFER,
};

export function FAQTiles() {
  const { t } = useTranslation('translation', { keyPrefix: 'support&FAQs.faqCategories' });
  const navigate = useNavigate();
  const screenSize = useBreakpoints();
  const theme = useTheme();

  const {
    flags: { [FlagKey.homeownerTransferSelfService]: showAgreementTransfer },
  } = useFeatureFlags();

  const [searchQuery, setSearchQuery] = useState('');
  const [displaySearchResults, setDisplaySearchResults] = useState(false);

  const isLargeScreen = screenSize === 'lg' || screenSize === 'xl';

  const faqTiles = useMemo(() => {
    const tiles = [
      {
        label: t('billPayments.title'),
        description: t('billPayments.description'),
        category: FAQCategory.PAYING_BILL,
        icon: <FAQTileGraphic category={FAQCategory.PAYING_BILL} />,
      },
      {
        label: t('paymentCalculations.title'),
        description: t('paymentCalculations.description'),
        category: FAQCategory.PAYMENT_CALCULATIONS,
        icon: <FAQTileGraphic category={FAQCategory.PAYMENT_CALCULATIONS} />,
      },
      {
        label: t('systemPerformance.title'),
        description: t('systemPerformance.description'),
        category: FAQCategory.SYSTEM_PERFORMANCE,
        icon: <FAQTileGraphic category={FAQCategory.SYSTEM_PERFORMANCE} />,
      },
      {
        label: t('systemChanges.title'),
        description: t('systemChanges.description'),
        category: FAQCategory.SYSTEM_CHANGES,
        icon: <FAQTileGraphic category={FAQCategory.SYSTEM_CHANGES} />,
      },
      {
        label: t('account.title'),
        description: t('account.description'),
        category: FAQCategory.ACCOUNT,
        icon: <FAQTileGraphic category={FAQCategory.ACCOUNT} />,
      },
      {
        label: t('reamortization.title'),
        description: t('reamortization.description'),
        category: FAQCategory.REAMORTIZATION,
        icon: <FAQTileGraphic category={FAQCategory.REAMORTIZATION} />,
      },
      {
        label: t('videoResources.title', { keyPrefix: 'support&FAQs' }),
        description: t('videoResources.copy', { keyPrefix: 'support&FAQs' }),
        category: FAQCategory.VIDEO_RESOURCES,
        icon: <FAQTileGraphic category={FAQCategory.VIDEO_RESOURCES} isLaptop />,
      },
    ];

    // Only include Agreement Transfer tile if feature flag is enabled
    if (showAgreementTransfer) {
      tiles.push({
        label: t('agreementTransfer.title'),
        description: t('agreementTransfer.description'),
        category: FAQCategory.AGREEMENT_TRANSFER,
        icon: <FAQTileGraphic category={FAQCategory.AGREEMENT_TRANSFER} />,
      });
    }

    return tiles;
  }, [t, showAgreementTransfer]);

  const handleClick = (category: FAQCategory) => {
    navigate(resolvePath(`${pages.SUPPORT}/${PAGES_BY_FAQ_CATEGORY[category]}`).pathname);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, category: FAQCategory) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleClick(category);
    }
  };

  return (
    <FAQPageWrapper>
      <FAQSearchBar onChange={setSearchQuery} onSearch={() => setDisplaySearchResults(true)} />
      {displaySearchResults ? (
        <FAQSearchResults
          searchQuery={searchQuery}
          onHideSearchResults={() => setDisplaySearchResults(false)}
          isVisible={displaySearchResults}
        />
      ) : (
        <FAQTilesWrapper>
          {faqTiles.map((item) => (
            <FAQTile
              onClick={() => handleClick(item.category)}
              onKeyDown={(e) => handleKeyDown(e, item.category)}
              tabIndex={0}
            >
              <Suspense fallback={<LoadingSpinner />}>{item.icon}</Suspense>
              <FAQTileContent>
                <Text
                  as="h3"
                  $margin={`0 0 ${isLargeScreen ? '14px' : '10px'}`}
                  $fontWeight={isLargeScreen ? 500 : 700}
                >
                  {item.label}
                </Text>
                <Text as="p" color={theme.colors.textSecondaryGrey90}>
                  {item.description}
                </Text>
              </FAQTileContent>
            </FAQTile>
          ))}
        </FAQTilesWrapper>
      )}
    </FAQPageWrapper>
  );
}

export default FAQTiles;
